import { useKeycloak } from '@react-keycloak/ssr';
import { useOrganizationData } from '@/contexts/OrganizationContext';
import { hasCommittedSessionIdentity } from '@/session/token';
import { useSessionIdentityGeneration } from '@/session/useSessionIdentityGeneration';
import { canAccessSettlements } from '@/utils/settlementsAccess';

export type SettlementsAccess = {
  // The viewer may see the channel-settlement money surfaces.
  allowed: boolean;
  // Neither grant has been decided yet. A gate that DENIES (an access message, a redirect) must
  // hold on this; a gate that merely HIDES (a nav entry, a tab) may ignore it, because hiding on a
  // pre-commit render is self-correcting — see "Super Admin Role Gating" in DESIGN.md.
  resolving: boolean;
};

// Owns both halves of the settlements gate so no call site has to wire them together.
//  - Realm roles are a module-level snapshot read, so this subscribes via useSessionIdentityGeneration
//    and the consumer re-renders on the identity commit that publishes them.
//  - hasTag is false until the organization membership list publishes, which means "not yet" rather
//    than "no" — hence `resolving`.
// Because `resolving` is qualified by `!allowed`, a viewer already granted by a staff role is never
// stranded by a slow or failed organization-list fetch: a later input landing cannot revoke a grant
// that has already been decided.
export const useSettlementsAccess = (): SettlementsAccess => {
  const { initialized } = useKeycloak();
  const { hasTag, organization } = useOrganizationData();
  useSessionIdentityGeneration();

  const allowed = canAccessSettlements(hasTag);
  const resolving = !allowed && (!initialized || !hasCommittedSessionIdentity() || organization === null);

  return { allowed, resolving };
};
